/** Short, question-specific help for informational AI Agent / Help queries. */

function norm(text: string): string {
  return text.trim().toLowerCase().replace(/\s+/g, ' ');
}

export function isAgentInformationalQuery(text: string): boolean {
  const t = norm(text);
  if (!t || t.length > 280) return false;
  return (
    /^(how|what|why|when|where|explain|tell me|describe|can you)\b/.test(t) ||
    /\bhow (does|do|can|to|should)\b/.test(t) ||
    /\bwhat is\b/.test(t) ||
    /\bworks?\??$/.test(t)
  );
}

const ROUND_ROBIN = `**Round-robin by data set** runs your collection in cycles across data-set rows.

1. Each request with data sets advances one row at a time across the collection (row 0 for all requests, then row 1, and so on).
2. Requests without data sets still run once per round.
3. **Iterations** repeat the whole round-robin cycle that many times (useful for soak or repeat runs).
4. In **Collections**, open a collection, choose **Round-robin by data set**, pick an environment, then run.

Use this when you want every request to consume the same data-set row index before moving to the next row.`;

const REQUEST_BY_REQUEST = `**Request by request** runs the collection in list order — each request finishes completely before the next one starts.

1. Open **Collections** and select your project and collection.
2. Choose **Request by request** as the run mode.
3. Select an environment (and proxy if needed), then run the collection or send individual requests.
4. Use data sets when one request needs multiple rows; each request executes all of its rows before the runner moves on.

Use this for linear flows where later calls depend on earlier responses.`;

const EXECUTE_REQUEST = `**Execute a single request**

1. Open **Collections** and pick project → collection → request.
2. Choose an environment so \`{{variables}}\` resolve.
3. Fill URL, headers, and body (or pick a data-set row).
4. Click **Send** (or run the whole collection from the run panel).

Tip: use **Console** to inspect the last response and **History** to review past runs.`;

const CUSTOM_FUNCTION = `**Custom functions** (Functions page) let you reuse logic as \`$[functionName]\` in URLs, headers, and bodies.

1. Open **Functions** → **Create custom function** (JavaScript or static value).
2. Save — enter only the name (e.g. \`generateemail\`); do not type \`$\` or \`$[]\`.
3. Reference it in Collections as \`$[generateemail]\` in a JSON body (plain \`$generateemail\` stays literal).

The runner evaluates functions when you send or run collections.`;

function matchTopic(t: string): string | null {
  if (
    t.includes('round robin') ||
    t.includes('round-robin') ||
    (t.includes('round') && t.includes('robin'))
  ) {
    return ROUND_ROBIN;
  }
  if (
    t.includes('request by request') ||
    t.includes('request-by-request') ||
    (t.includes('request') && t.includes('by request'))
  ) {
    return REQUEST_BY_REQUEST;
  }
  if (
    t.includes('execute') ||
    t.includes('send request') ||
    t.includes('run request') ||
    t.includes('how can i') ||
    t.includes('how to')
  ) {
    if (t.includes('collection') || t.includes('round') || t.includes('request by')) {
      return null;
    }
    return EXECUTE_REQUEST;
  }
  if (t.includes('custom function') || (t.includes('function') && (t.includes('create') || t.includes('$')))) {
    return CUSTOM_FUNCTION;
  }
  if (t.includes('collection') && (t.includes('run') || t.includes('execute') || t.includes('how'))) {
    return `${REQUEST_BY_REQUEST}\n\n---\n\n${ROUND_ROBIN}`;
  }
  return null;
}

export function getAgentInfoHelpAnswer(text: string): string | null {
  if (!isAgentInformationalQuery(text)) return null;
  return matchTopic(norm(text));
}

export function isGenericHelpResponse(answer: string, question: string): boolean {
  const a = answer.trim();
  if (!a) return true;
  const lower = a.toLowerCase();
  if (lower.includes('**goal**') && lower.includes('**steps**')) return true;
  if (lower.includes('select a project') && !norm(question).includes('project')) return true;
  if (a.length < 120 && lower.includes('open collections') && isAgentInformationalQuery(question)) {
    return true;
  }
  return false;
}

const AGENT_CACHED_CHAT_MESSAGES_KEY = 'ai-agent-cached-chat-messages';

export type AgentCachedChatMessage = {
  role: string;
  text: string;
  isSuccess?: boolean;
  isFailure?: boolean;
};

function cachedChatMessagesStorageKey(chatId: number | null | undefined): string {
  return `${AGENT_CACHED_CHAT_MESSAGES_KEY}:${chatId ?? 'new'}`;
}

function cachedMessageKey(message: { role?: string; text?: string }): string {
  return `${String(message.role ?? '')}:${String(message.text ?? '').trim()}`;
}

export function readAgentCachedChatMessages(
  chatId: number | null | undefined,
): AgentCachedChatMessage[] {
  try {
    const raw = sessionStorage.getItem(cachedChatMessagesStorageKey(chatId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((row) => {
        if (!row || typeof row !== 'object') return null;
        const role = typeof row.role === 'string' ? row.role : '';
        const text = typeof row.text === 'string' ? row.text : '';
        if (!role || !text) return null;
        return {
          role,
          text,
          isSuccess: row.isSuccess === true,
          isFailure: row.isFailure === true,
        } as AgentCachedChatMessage;
      })
      .filter((row): row is AgentCachedChatMessage => row != null);
  } catch {
    return [];
  }
}

export function writeAgentCachedChatMessages(
  chatId: number | null | undefined,
  messages: Array<{ role?: string; text?: string; isSuccess?: boolean; isFailure?: boolean }>,
): void {
  try {
    const key = cachedChatMessagesStorageKey(chatId);
    const rows = (messages ?? [])
      .map((m) => ({
        role: String(m.role ?? ''),
        text: String(m.text ?? ''),
        isSuccess: m.isSuccess === true,
        isFailure: m.isFailure === true,
      }))
      .filter((m) => m.role && m.text);
    if (rows.length === 0) return;
    sessionStorage.setItem(key, JSON.stringify(rows));
  } catch {
    /* ignore */
  }
}

/** Keep locally generated replies (e.g. "what is round robin") when chat history reloads. */
export function mergeAgentCachedChatMessages<T extends { role?: string; text?: string }>(
  serverMessages: T[] | null | undefined,
  cachedMessages: T[] | null | undefined,
): T[] {
  const server = Array.isArray(serverMessages) ? serverMessages : [];
  const cached = Array.isArray(cachedMessages) ? cachedMessages : [];
  if (cached.length === 0) return server;
  if (server.length === 0) return cached;
  const cachedKeys = new Set(cached.map(cachedMessageKey));
  const extraFromServer = server.filter((m) => !cachedKeys.has(cachedMessageKey(m)));
  return extraFromServer.length === 0 ? cached : [...cached, ...extraFromServer];
}
